import { SortDescriptor } from '@nextui-org/react';
import {
  Dispatch,
  Key,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { IParceiro } from '../../../../interfaces/response/Parceiros/IParceiro';

type Column = {
  key: string;
  name: string;
};

export interface UseParceirosTableProps {
  parceiros: IParceiro[];
  onRowClick?: (parceiro: IParceiro) => void;
  filter?: string;
}

export interface UseParceirosTable {
  handleRowClick: (key: Key) => void;
  sortDescriptor: SortDescriptor;
  setSortDescriptor: Dispatch<SetStateAction<SortDescriptor>>;
  columns: Column[];
  cellData: (parceiro: IParceiro, columnKey: string) => string;
  pagedParceiros: IParceiro[];
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  totalPages: number;
  totalCount: number;
}

const PAGE_SIZE = 25;

const normalizeText = (value: string): string =>
  (value ?? '').trim().toLowerCase();

const normalizeCep = (cep: string): string => (cep ?? '').replace(/\D/g, '');

const formatCep = (cep: string): string => {
  const digits = normalizeCep(cep);
  if (digits.length !== 8) return cep ?? '';
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const getRowKey = (p: IParceiro): string => {
  const fallback = `${p.sigla ?? ''}-${p.cepInicio ?? ''}-${p.cepFim ?? ''}`;
  return (p.id ?? fallback) as string;
};

const useParceirosTable = ({
  parceiros,
  onRowClick,
  filter = '',
}: UseParceirosTableProps): UseParceirosTable => {
  const [page, setPage] = useState<number>(1);

  // 🔥 ORDEM PADRÃO: CEP INÍCIO CRESCENTE
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'cepInicio',
    direction: 'ascending',
  });

  useEffect(() => {
    setPage(1);
  }, [parceiros]);

  useEffect(() => {
    setPage(1);
  }, [filter]);

  const columns: Column[] = [
    { key: 'sigla', name: 'SIGLA' },
    { key: 'cepInicio', name: 'CEP INÍCIO' },
    { key: 'cepFim', name: 'CEP FIM' },
  ];

  // 1) FILTRO
  const filteredParceiros = useMemo(() => {
    const q = normalizeText(filter);
    if (!q) return parceiros;

    const qDigits = normalizeCep(q);

    return parceiros.filter((p) => {
      const sigla = normalizeText(p.sigla ?? '');

      if (sigla.includes(q)) return true;

      if (qDigits) {
        const ini = normalizeCep(p.cepInicio ?? '');
        const fim = normalizeCep(p.cepFim ?? '');
        return ini.includes(qDigits) || fim.includes(qDigits);
      }

      return false;
    });
  }, [parceiros, filter]);

  // 2) ORDENAÇÃO (CEP COMO NÚMERO)
  const sortedParceiros = useMemo(() => {
    const col = (sortDescriptor.column ?? 'cepInicio') as keyof IParceiro;
    const dir = sortDescriptor.direction ?? 'ascending';

    return [...filteredParceiros].sort((a, b) => {
      // Se for CEP, compara numericamente
      if (col === 'cepInicio' || col === 'cepFim') {
        const av = Number(normalizeCep(String(a[col] ?? '0')));
        const bv = Number(normalizeCep(String(b[col] ?? '0')));
        const cmp = av - bv;
        return dir === 'ascending' ? cmp : -cmp;
      }

      // Demais colunas: string normal
      const av = String(a[col] ?? '').toLowerCase();
      const bv = String(b[col] ?? '').toLowerCase();
      const cmp = av.localeCompare(bv);
      return dir === 'ascending' ? cmp : -cmp;
    });
  }, [filteredParceiros, sortDescriptor.column, sortDescriptor.direction]);

  // 3) PAGINAÇÃO
  const totalPages = useMemo(() => {
    const pages = Math.ceil(sortedParceiros.length / PAGE_SIZE);
    return pages <= 0 ? 1 : pages;
  }, [sortedParceiros.length]);

  const pagedParceiros = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return sortedParceiros.slice(start, end);
  }, [page, sortedParceiros]);

  const cellData = useCallback(
    (parceiro: IParceiro, columnKey: string): string => {
      switch (columnKey) {
        case 'id':
          return String(parceiro.id ?? '');
        case 'sigla':
          return (parceiro.sigla ?? '').toUpperCase();
        case 'cepInicio':
          return formatCep(parceiro.cepInicio ?? '');
        case 'cepFim':
          return formatCep(parceiro.cepFim ?? '');
        default:
          return '';
      }
    },
    []
  );

  const handleRowClick = (key: Key): void => {
    const k = String(key);

    const parceiro =
      parceiros.find((p) => p.id && p.id === k) ??
      parceiros.find((p) => getRowKey(p) === k);

    if (onRowClick && parceiro) onRowClick(parceiro);
  };

  return {
    columns,
    cellData,
    handleRowClick,
    pagedParceiros,
    page,
    setPage,
    setSortDescriptor,
    sortDescriptor,
    totalPages,
    totalCount: sortedParceiros.length,
  };
};

export default useParceirosTable;
